import { inject, Injectable } from '@angular/core';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { DeviceLockManagementModalComponent } from '../../components/device-lock-management-modal/device-lock-management-modal.component';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { ReportIssueService } from '../report-issue/report-issue.service';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import {
    DEVICE_LOCK_CLIENTID,
    DEVICE_LOCK_CLIENTID_CREATED_AT,
    DEVICE_LOCK_KEY,
    DeviceLockFingerprint,
    EMPTY_DEVICE_LOCK,
    INITIAL_LOCK_STATE,
    isDeterministicFingerprintEqual,
    loadLocalLock,
    LockState,
    LockValue,
    lockValueToState,
    saveLocalLock,
} from './device.utils';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { LogInstanceToken, PaceInsightsService } from '@evorait/pace-logging';
import { safeStringify } from '@evorait/pace-components';
import { UAParser } from 'ua-parser-js';
import { BsModalService } from 'ngx-bootstrap/modal';
import { detectIncognito } from 'detectincognitojs';
import { EnvironmentService } from '@evorait/pace-environment';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';

@Injectable({
    providedIn: 'root',
})
export class DeviceLockService implements InitializableInterface {
    private readonly log = inject(LogInstanceToken);
    private readonly concurrentUsageService = inject(ConcurrentUsageService);
    private readonly connectivityService = inject(ConnectivityService);
    private readonly paceInsights = inject(PaceInsightsService);
    private readonly modalService = inject(BsModalService);
    private readonly configService = inject(ConfigInstanceToken);
    private readonly reportIssueService = inject(ReportIssueService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly clientManager = inject(ClientManagerInstanceToken);

    private readonly lockState = new BehaviorSubject<LockState>(INITIAL_LOCK_STATE);
    private readonly localFingerPrint: DeviceLockFingerprint;

    public isInitialized = false;

    private lockModalOpen = false;

    private readonly isReleaseLockInProgressSubject = new BehaviorSubject(false);

    public readonly lockState$ = this.lockState
        .asObservable()
        .pipe(
            distinctUntilChanged(
                (a, b) =>
                    a.ownedByMe === b.ownedByMe &&
                    a.ownerFingerprint?.clientId === b.ownerFingerprint?.clientId &&
                    a.acquiredAt === b.acquiredAt,
            ),
        );

    public constructor() {
        const { os, device, browser } = new UAParser().getResult();
        this.localFingerPrint = {
            os: os.name,
            vendor: device.vendor,
            model: device.model,
            browser: browser.name,
            clientId: this.getClientId(),
            isPrivateBrowser: false,
            standalone: this.clientManager.isPacePWAStandalone(),
        };
    }

    public init(): Observable<unknown> {
        window.addEventListener('storage', (event) => {
            if (event.key === DEVICE_LOCK_KEY) {
                const localValue = loadLocalLock();
                const localState = lockValueToState(localValue, this.deviceFingerprint);
                this.paceInsights.trackEvent('DEVICE_LOCK_UPDATE_AFTER_LOCALSTORAGE_EVENT', {
                    ...localState,
                    localId: this.deviceFingerprint,
                });
                this.log.info(
                    this.constructor.name + ': init storage event listener: lock changed. reading lock locally',
                    localState,
                );
                this.lockState.next(localState);

                this.log.info('storage event on lock state.', {
                    key: event.key,
                    newValue: event.newValue,
                    oldValue: event.oldValue,
                    url: event.url,
                });
                if (!localState.ownedByMe && !this.lockModalOpen) {
                    this.log.info(
                        'lock state changed in local storage, but is not owned by me anymore. need to show lock screen',
                    );

                    this.modalService.show(DeviceLockManagementModalComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        ariaLabelledBy: 'app-notification-modal-title',
                        keyboard: false,
                        ignoreBackdropClick: true,
                        closeInterceptor: () => {
                            return new Promise((resolve, reject) => {
                                this.log.warn("User want's to close device lock screen, which is not allowed");
                                reject();
                            });
                        },
                    });
                }
            }
        });

        return this.setPrivateSessionState().pipe(
            switchMap(() => this.connectivityService.isOnline$),
            take(1), // connectivityService.isOnline$ is a not-completing stream and can retrigger subsequent elements in this pipeline.
            switchMap((online) => {
                if (!online) {
                    const localValue = loadLocalLock();
                    const localState = lockValueToState(localValue, this.deviceFingerprint);
                    this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_WO_INTERNET', {
                        ...localState,
                        localId: this.deviceFingerprint,
                    });
                    this.log.info(this.constructor.name + ': init: device is offline, read lock locally', localState);
                    this.lockState.next(localState);
                    return of(!!localState?.ownedByMe);
                }

                // online must revalidate
                return this.tryAcquireLockAtInit();
            }),
            map((haveLock) => {
                this.paceInsights.trackEvent('DEVICE_LOCK_INIT_RESULTS', {
                    haveLock,
                    localId: this.deviceFingerprint,
                });

                if (!haveLock) {
                    this.log.warn(this.constructor.name + ': init: failed - do not have the device lock!');
                    throw new Error('This device does not have the device lock.');
                }
                this.isInitialized = true;
            }),
        );
    }

    public setLockModalOpen(open: boolean): void {
        this.lockModalOpen = open;
    }

    public updateReleaseLockStatus(isReleaseLockInProgress: boolean): void {
        this.isReleaseLockInProgressSubject.next(isReleaseLockInProgress);
    }

    private tryAcquireLockAtInit(): Observable<boolean> {
        return this.refreshState().pipe(
            switchMap((state) => {
                if (state.ownedByMe) {
                    return of(true);
                }
                if (state.ownerFingerprint?.clientId) {
                    return of(false);
                }
                return this.concurrentUsageService.downloadAll().pipe(switchMap(() => this.acquire()));
            }),
        );
    }

    /**
     * Tries to acquire the lock for the current client.
     *
     * Will write a new lock entry with a fresh token and acquisition timestamp if there is no lock, yet.
     *
     * @returns Observable<boolean> that emits:
     * - `true`  if the lock is (after this call) owned by the current client
     * - `false` if the lock is owned by another client or could not be acquired
     */
    public acquire(): Observable<boolean> {
        this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_ATTEMPT', { localId: this.deviceFingerprint });
        this.log.debug(this.constructor.name + ': acquire: attempting to acquire device lock');

        return this.refreshState().pipe(
            switchMap((current) => {
                this.log.debug(this.constructor.name + ': acquire: got current state', current);
                if (current?.ownedByMe) {
                    // already owned by me! no need to re-acquire
                    return of(true);
                }

                if (current?.ownerFingerprint?.clientId) {
                    this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_FAILED', {
                        ...current,
                        localId: this.deviceFingerprint,
                    });
                    this.log.warn(this.constructor.name + ': acquire: failed - locked by other device!', {
                        ...current,
                        localId: this.deviceFingerprint,
                    });
                    // foreign owner means hard fail, no write
                    return of(false);
                }

                const now = new Date().toISOString();

                const newValue: LockValue = {
                    fingerprint: this.deviceFingerprint,
                    acquiredAt: now,
                };

                this.log.debug(this.constructor.name + ': acquire: writing new lock to conc store', newValue);

                // write and verify
                return this.writeRemoteLock(newValue).pipe(
                    switchMap(() => this.refreshState()),
                    map((stored) => stored?.ownedByMe),
                    tap((success) => {
                        this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_RESULT', {
                            success,
                            localId: this.deviceFingerprint,
                        });
                        if (success) {
                            this.log.debug(this.constructor.name + ': acquire: successfully acquired lock');
                        } else {
                            this.log.error(this.constructor.name + ': acquire: failed to acquire lock');
                        }
                    }),
                );
            }),
        );
    }

    /**
     * Attempts to release the currently held lock for this client.
     *
     * If the lock is owned by this client it will be cleared with an 'empty' lock.
     * Always refreshes the internal lock state from storage.
     *
     * @returns Observable<boolean> that emits:
     * - `true` if the lock was successfully released (no owner after refresh).
     * - `false` if this client did not own the lock or releasing failed.
     */
    public release(): Observable<boolean> {
        this.log.debug(this.constructor.name + ': release: attempting to release device lock');
        return this.refreshState().pipe(
            switchMap((current) => {
                if (!current?.ownedByMe) {
                    // we're not the owner and won't overwrite!
                    this.log.warn(this.constructor.name + ': release: cannot release, not owned by me', current);
                    return of(false);
                }

                return this.writeRemoteLock(EMPTY_DEVICE_LOCK).pipe(
                    switchMap(() => this.refreshState()),
                    map((stored) => !stored?.ownerFingerprint),
                );
            }),
        );
    }

    /**
     * Refreshes and emits the current lock state.
     *
     * Reads the lock from remote and maps it into a `LockState` object.
     * The resulting state will be emitted through  `lockState$`.
     *
     * @returns Observable<LockState>  The updated lock state.
     */
    public refreshState(): Observable<LockState> {
        return this.readRemoteLock().pipe(
            map((v) => lockValueToState(v, this.deviceFingerprint)),
            tap((state) => this.lockState.next(state)),
        );
    }

    private getClientId(): string {
        const now = Date.now();

        const storedClientId = localStorage.getItem(DEVICE_LOCK_CLIENTID);
        const createdAtRaw = localStorage.getItem(DEVICE_LOCK_CLIENTID_CREATED_AT);

        const clientId = storedClientId ?? getUuidV4();
        const createdAt = createdAtRaw ? Number(createdAtRaw) : null;

        if (!storedClientId) {
            if (createdAtRaw) {
                this.log.error(
                    'STORAGE CORRUPTED? found a client id creating date, but not the client ID. that must not happen!',
                    { storedClientId, createdAtRaw, createdAt },
                );
            }
            localStorage.setItem(DEVICE_LOCK_CLIENTID, clientId);
            localStorage.setItem(DEVICE_LOCK_CLIENTID_CREATED_AT, String(now));
        }

        detectIncognito()
            .then((incognito) => {
                const ageMs = createdAt ? now - createdAt : null;
                const ageMinutes = ageMs !== null ? Math.floor(ageMs / 60000) : null;
                const ageHours = ageMs !== null ? Math.floor(ageMs / (60 * 60 * 1000)) : null;
                const ageDays = ageMs !== null ? Math.floor(ageMs / (24 * 60 * 60 * 1000)) : null;

                this.paceInsights.trackEvent('DEVICE_LOCK_CLIENT_ID', {
                    clientId,
                    createdAt,
                    createdAtRaw,
                    incognito,
                    found: !!storedClientId,
                    userAgent: window.navigator.userAgent,
                    deviceFingerprint: this.deviceFingerprint,
                    age: {
                        humanReadable: `${ageDays}d ${ageHours}h ${ageMinutes}m`,
                        ageDays,
                        ageHours,
                        ageMinutes,
                        ageMs,
                    },
                });
            })
            .catch((error) =>
                this.log.error('failed to detect incognito mode for tracking client id event', JSON.stringify(error)),
            );
        return clientId;
    }

    public get deviceFingerprint(): DeviceLockFingerprint {
        return this.localFingerPrint;
    }

    private readRemoteLock(): Observable<LockValue | null> {
        return this.concurrentUsageService.readRemote(DEVICE_LOCK_KEY).pipe(
            map((res) => {
                if (res?.length > 0) {
                    try {
                        return JSON.parse(res[0].value) as LockValue;
                    } catch (e) {
                        this.log.error(
                            this.constructor.name + ': readRemoteLock: failed to parse lock from concurrent store',
                            JSON.stringify(e),
                        );
                        throw new Error(
                            `failed to parse lock from concurrent store: ${safeStringify(res)} -- ${safeStringify(e)}`,
                        );
                    }
                }
                return null;
            }),
            tap((lock) => {
                this.log.debug(this.constructor.name + ': readRemoteLock: read lock from concurrent store', lock);
                saveLocalLock(lock);
            }),
        );
    }

    private writeRemoteLock(value: LockValue): Observable<unknown> {
        this.log.debug(this.constructor.name + ': writeRemoteLock: writing lock to concurrent store', value);
        return this.concurrentUsageService
            .writeRemote({
                key: DEVICE_LOCK_KEY,
                value: JSON.stringify(value),
                timestamp: Date.now(),
            })
            .pipe(
                tap(() => {
                    // Fix for the mock mode. Here we are checking, if a local instance is running. Only then we modify the executing code.
                    if (this.environmentService.operateInMockMode) {
                        saveLocalLock(value);
                    }
                }),
            );
    }

    public forceRemoveLock(): Observable<unknown> {
        if (window.location.hostname === 'localhost') {
            return this.writeRemoteLock(EMPTY_DEVICE_LOCK);
        } else {
            return this.refreshState().pipe(
                switchMap(() => this.lockState$),
                take(1),
                filter((state) => this.isDeviceLockAllowedToBeRemoved(state)),
                switchMap((state) => this.reportIssueService.logForceDeviceUnlock(state, this.deviceFingerprint)),
                switchMap(() => this.writeRemoteLock(EMPTY_DEVICE_LOCK)),
            );
        }
    }

    /**
     * If the client ID has changed, but the device information is the same, it is likely that the user has cleared the
     * browser cache, locking themselves out. This function checks for this state.
     *
     * @returns boolean
     * @param state : LockState containing info about the locking device
     */
    public isDeviceLockAllowedToBeRemoved(state: LockState): boolean {
        return (
            this.configService.getEnableForceCheckin() ||
            (state.ownerFingerprint?.clientId !== this.deviceFingerprint.clientId &&
                !!state.ownerFingerprint &&
                isDeterministicFingerprintEqual(state.ownerFingerprint, this.deviceFingerprint))
        );
    }

    private setPrivateSessionState(): Observable<boolean> {
        return from(detectIncognito()).pipe(
            map((result) => {
                this.log.info(this.constructor.name + ': setPrivateSessionState', { result });
                this.localFingerPrint.isPrivateBrowser = result.isPrivate;
                return result.isPrivate;
            }),
            catchError((error) => {
                this.log.error(
                    this.constructor.name + ': setPrivateSessionState: Private Browser detection tool failed',
                    { error: safeStringify(error) },
                );
                return of(false);
            }),
        );
    }

    public isLockActive(): boolean {
        return this.lockState.value.ownedByMe && !this.isReleaseLockInProgressSubject.value;
    }
}
